import { computed, inject, Injectable, InjectionToken, signal } from '@angular/core';
import { catchError, forkJoin, of } from 'rxjs';
import { InboxEntry } from '../../models/inbox-entry.model';
import { Benachrichtigungen } from '../../models/benachrichtigung.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { InboxService } from '../../services/inbox.service';
import { HeaderInboxBadgeService } from '../../services/header-inbox-badge.service';
import { AccountService } from '../../account/account.service';
import { artVon, AUFGABEN_ARTEN, AufgabenArt, AufgabenArtInfo, faelligkeit, sortiereAufgaben } from './aufgaben-art';

export const HEUTE = new InjectionToken<() => Date>('HEUTE', {
  providedIn: 'root',
  factory: () => () => new Date(),
});

export type AufgabenStatus = 'laedt' | 'fehler' | 'bereit';

@Injectable()
export class MeineAufgabenStore {
  private inboxService = inject(InboxService);
  private accountService = inject(AccountService);
  private inboxBadge = inject(HeaderInboxBadgeService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private jetzt = inject(HEUTE);

  private readonly _eintraege = signal<InboxEntry[]>([]);
  private readonly _status = signal<AufgabenStatus>('laedt');
  private readonly _filter = signal<AufgabenArt | null>(null);
  private readonly _kanaele = signal<Partial<Benachrichtigungen>>({});

  readonly eintraege = this._eintraege.asReadonly();
  readonly status = this._status.asReadonly();
  readonly filter = this._filter.asReadonly();
  readonly kanaele = this._kanaele.asReadonly();

  readonly kacheln = computed<AufgabenArtInfo[]>(() => AUFGABEN_ARTEN.filter((a) => this.imDashboard(a.art)));
  readonly nurMail = computed<AufgabenArtInfo[]>(() => AUFGABEN_ARTEN.filter((a) => !this.imDashboard(a.art)));

  readonly offene = computed<InboxEntry[]>(() =>
    this._eintraege().filter((eintrag) => {
      const art = artVon(eintrag);
      return art !== null && this.imDashboard(art);
    }),
  );

  readonly zaehler = computed<Record<AufgabenArt, number>>(() => {
    const zaehler: Record<AufgabenArt, number> = { uebergabe: 0, testlauf: 0, review: 0, abweichung: 0 };
    this.offene().forEach((eintrag) => (zaehler[artVon(eintrag)!] += 1));
    return zaehler;
  });

  readonly ueberfaellig = computed(() => this.anzahlMitFaelligkeit('ueberfaellig'));
  readonly heute = computed(() => this.anzahlMitFaelligkeit('heute'));

  readonly liste = computed<InboxEntry[]>(() => {
    const filter = this._filter();
    const gefiltert = filter ? this.offene().filter((eintrag) => artVon(eintrag) === filter) : this.offene();
    return sortiereAufgaben(gefiltert, this.jetzt());
  });

  imDashboard(art: AufgabenArt): boolean {
    return this._kanaele()[art] !== 'mail';
  }

  laden(): void {
    this._status.set('laedt');
    forkJoin({
      aufgaben: this.inboxService.aufgaben(),
      kanaele: this.accountService.getBenachrichtigungen().pipe(catchError(() => of<Partial<Benachrichtigungen>>({}))),
    }).subscribe({
      next: ({ aufgaben, kanaele }) => {
        this._eintraege.set(aufgaben);
        this._kanaele.set(kanaele);
        this._status.set('bereit');
      },
      error: () => this._status.set('fehler'),
    });
  }

  filterUmschalten(art: AufgabenArt): void {
    this._filter.update((aktuell) => (aktuell === art ? null : art));
  }

  erledigen(eintrag: InboxEntry): void {
    this.entfernen(eintrag.id);
    this.inboxService.markDone(eintrag.id).subscribe({
      next: () => this.inboxBadge.refresh(),
      error: () => {
        this.zuruecklegen(eintrag);
        this.snackBar.open(this.translate.instant('home.aufgaben.liste.erledigtFehler'), undefined, {
          duration: 4000,
          panelClass: 'snackbar-error',
        });
      },
    });
  }

  filterAufheben(): void {
    this._filter.set(null);
  }

  entfernen(id: string): void {
    this._eintraege.update((eintraege) => eintraege.filter((eintrag) => eintrag.id !== id));
  }

  // Die Sortierung der Liste bringt den Eintrag an seine alte Stelle zurück.
  private zuruecklegen(eintrag: InboxEntry): void {
    this._eintraege.update((eintraege) => [...eintraege, eintrag]);
  }

  private anzahlMitFaelligkeit(stufe: 'ueberfaellig' | 'heute'): number {
    const heute = this.jetzt();
    return this.offene().filter((eintrag) => faelligkeit(eintrag.dueAt ?? null, heute) === stufe).length;
  }
}
